/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';

import { RootState } from '../../../../../redux/store';
import {
  isPercentLessThanOne,
  isPercentRoundedToOneHundred
} from '../../../../../utils/complianceFunctions';

/**
 * Global Type Definition Imports
 */
import { ComplianceReport } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  CellDiv,
  StyledBoldLabel,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface IPatientComplianceReportTableProps {
  complianceList: ComplianceReport[];
  complianceTotal?: ComplianceReport | null;
  loading: boolean;
}
const PatientComplianceReportTable: FC<IPatientComplianceReportTableProps> = ({
  complianceList,
  complianceTotal,
  loading
}) => {
  const [sortKey, setSortKey] = useState<string>('unit');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Unit');

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Unit':
        key = 'unit';
        break;
      case 'Unit Type':
        key = 'unitType';
        break;
      case 'Census':
        key = 'census';
        break;
      case 'Rounded':
        key = 'roundedNumber';
        break;
      case 'Unable to Round':
        key = 'unableNumber';
        break;
      case 'Not Rounded':
        key = 'notRoundedNumber';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const sortedCompliance = useMemo(() => {
    const stateCopy = [...complianceList];
    const sorted = stateCopy.sort(
      (a: ComplianceReport, b: ComplianceReport) => {
        if (!(sortKey === 'unit' || sortKey === 'unitType')) {
          if (a[sortKey] === b[sortKey]) {
            return 0;
          }
          if (sortDir === 'asc') {
            return (a[sortKey] as number) - (b[sortKey] as number);
          }
          return (b[sortKey] as number) - (a[sortKey] as number);
        }
        if (a[sortKey] === b[sortKey]) {
          return 0;
        }
        if (!a[sortKey]) {
          return sortDir === 'asc' ? 1 : -1;
        }
        if (!b[sortKey]) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (sortDir === 'asc') {
          return a[sortKey]! > b[sortKey]! ? 1 : -1;
        }
        return a[sortKey]! > b[sortKey]! ? -1 : 1;
      }
    );
    return sorted;
  }, [complianceList, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => sortedCompliance.length === 0,
    [sortedCompliance]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Unit"
      id="Patient-Compliance-Unit-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Unit Type"
      id="Patient-Compliance-Unit-Type-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Unit Type') ? 'none' : sortDir}
      sort
    >
      Unit Type
    </CellContainer>,
    <CellContainer
      key="Census"
      id="Patient-Compliance-Census-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Census') ? 'none' : sortDir}
      sort
    >
      Census
    </CellContainer>,
    <CellContainer
      key="Rounded #"
      id="Patient-Compliance-Rounded-#-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Rounded') ? 'none' : sortDir}
      sort
    >
      Rounded
    </CellContainer>,
    <CellContainer
      key="Unable to Round #"
      id="Patient-Compliance-Unable-#-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Unable to Round') ? 'none' : sortDir}
      sort
    >
      Unable to Round
    </CellContainer>,
    <CellContainer
      key="Not Rounded #"
      id="Patient-Compliance-Not-Rounded-#-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Not Rounded') ? 'none' : sortDir}
      sort
    >
      Not Rounded
    </CellContainer>
  ];

  const rows = (
    complianceTotal
      ? [{ ...complianceTotal, totalRow: true }, ...sortedCompliance]
      : sortedCompliance
  ).map((item: ComplianceReport, i: number) => {
    const isRoundedPercentLessThanOne = isPercentLessThanOne(
      item.roundedNumber,
      item.census
    );
    const isUnablePercentLessThanOne = isPercentLessThanOne(
      item.unableNumber,
      item.roundedNumber
    );
    const isNotRoundedPercentLessThanOne = isPercentLessThanOne(
      item.notRoundedNumber,
      item.census
    );
    const isRoundedPercentRoundedToOneHundred = isPercentRoundedToOneHundred(
      item.roundedNumber,
      item.census
    );
    const isUnablePercentRoundedToOneHundred = isPercentRoundedToOneHundred(
      item.unableNumber,
      item.roundedNumber
    );
    const isNotRoundedPercentRoundedToOneHundred = isPercentRoundedToOneHundred(
      item.notRoundedNumber,
      item.census
    );

    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Patient-Compliance-Report-Row-${i}`}
        id={`Patient-Compliance-Report-Row-${item.unitId}`}
        data-testid={`Patient-Compliance-Report-Row-${item.unitId}`}
        unit="row"
        size="default"
        height="5em"
        styles={item.totalRow ? 'color: #093865; font-weight: 700;' : ''}
      >
        <CellContainer unit="column" colWidth={25}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontWeight: 700
                }}
              >
                {item.unit}
              </div>
              <div>{!item.unit.includes('Total') ? item.unitId : ''}</div>
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>{item.unitType}</div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            <div>{item.census}</div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <CellDiv>
            <StyledBoldLabel>{item.roundedNumber}</StyledBoldLabel>
            <StyledLabel className={!isUnablePercentLessThanOne ? 'ps-1' : ''}>
              {isRoundedPercentLessThanOne
                ? '<'
                : isRoundedPercentRoundedToOneHundred
                ? '>'
                : ''}
              {item.roundedPercent}%
            </StyledLabel>
          </CellDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <CellDiv>
            <StyledBoldLabel>{item.unableNumber}</StyledBoldLabel>
            <StyledLabel className={!isUnablePercentLessThanOne ? 'ps-1' : ''}>
              {isUnablePercentLessThanOne
                ? '<'
                : isUnablePercentRoundedToOneHundred
                ? '>'
                : ''}
              {item.unablePercent}%
            </StyledLabel>
          </CellDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <CellDiv>
            <StyledBoldLabel>{item.notRoundedNumber}</StyledBoldLabel>
            <StyledLabel
              className={!isNotRoundedPercentLessThanOne ? 'ps-1' : ''}
            >
              {isNotRoundedPercentLessThanOne
                ? '<'
                : isNotRoundedPercentRoundedToOneHundred
                ? '>'
                : ''}
              {item.notRoundedPercent}%
            </StyledLabel>
          </CellDiv>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="patient-compliance-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={loading}
      offset="70px"
      type="patientComplianceReport"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    loading: state.ReportReducer.loading
  };
};

export default connect(mapReduxStateToProps)(PatientComplianceReportTable);
